import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import ResizableImage from '../components/ResizableImageExtension';
import api from '../utils/api';
import { ArrowLeft, Pencil, Trash2, Check, X } from 'lucide-react';
import EditorToolbar from '../components/EditorToolbar';
import FileAttachments from '../components/FileAttachments';

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await api.post('/boards/upload', fd);
  return r.data.url;
}

export default function ResearchLogEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [log, setLog] = useState(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const fileInputRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('paper_work_user') || '{}');

  const editor = useEditor({
    extensions: [
      StarterKit,
      ResizableImage,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: '내용을 입력하세요...' }),
    ],
    editorProps: {
      attributes: { class: 'tiptap !min-h-[300px] !p-4' },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (!file) continue;
            (async () => {
              const url = await uploadImage(file);
              view.dispatch(view.state.tr.replaceSelectionWith(
                view.state.schema.nodes.image.create({ src: url })
              ));
            })();
            return true;
          }
        }
        return false;
      },
    },
    editable: false,
  });

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files?.length || !editor) return;
    for (const f of files) {
      if (!f.type.startsWith('image/')) continue;
      const url = await uploadImage(f);
      editor.chain().focus().setImage({ src: url }).run();
    }
    e.target.value = '';
  };

  const load = async () => {
    const r = await api.get(`/research-logs/${id}`);
    setLog(r.data);
    setTitle(r.data.title);
    if (editor && !editor.isDestroyed) {
      editor.commands.setContent(r.data.content || '');
    }
  };

  useEffect(() => { load(); }, [id]);

  // editor가 준비된 후 content 주입
  useEffect(() => {
    if (editor && log) {
      editor.commands.setContent(log.content || '');
    }
  }, [editor, log?.id]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editing);
    if (editing) {
      setTimeout(() => editor.commands.focus(), 50);
    }
  }, [editing, editor]);

  const startEdit = () => setEditing(true);

  const save = async () => {
    const content = editor?.getHTML() || '';
    await api.put(`/research-logs/${id}`, { title, content });
    setEditing(false);
    load();
  };

  const cancelEdit = () => {
    setEditing(false);
    setTitle(log.title);
    editor?.commands.setContent(log.content || '');
  };

  const remove = async () => {
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/research-logs/${id}`);
    navigate('/research');
  };

  const canEdit = user.role === 'superadmin' || user.role === 'admin' || log?.createdBy === user.id;
  const fmt = (d) => new Date(d).toLocaleString('ko-KR');

  if (!log) return <div className="text-gray-600 text-sm p-8">불러오는 중...</div>;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* 헤더 */}
      <div className="flex-shrink-0 border-b border-gray-800 bg-gray-950">
        <div className="flex items-center justify-between px-4 py-2.5 gap-3">
          <button onClick={() => navigate('/research')}
            className="flex items-center gap-1.5 text-gray-500 hover:text-white text-sm transition-colors flex-shrink-0">
            <ArrowLeft size={15} /> 목록
          </button>

          <div className="flex-1 min-w-0">
            {editing ? (
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                autoFocus
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm font-semibold focus:outline-none focus:border-brand"
              />
            ) : (
              <h1 className="text-white font-bold text-base truncate">{log.title}</h1>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {editing ? (
              <>
                <button onClick={save} className="flex items-center gap-1 px-3 py-1.5 bg-brand text-white text-xs rounded-lg hover:bg-brand/80">
                  <Check size={12} /> 저장
                </button>
                <button onClick={cancelEdit} className="p-1.5 text-gray-500 hover:text-white">
                  <X size={14} />
                </button>
              </>
            ) : canEdit && (
              <>
                <button onClick={startEdit} className="p-1.5 text-gray-500 hover:text-white rounded-md hover:bg-gray-800">
                  <Pencil size={14} />
                </button>
                <button onClick={remove} className="p-1.5 text-gray-500 hover:text-red-400 rounded-md hover:bg-gray-800">
                  <Trash2 size={14} />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="px-5 pb-2.5 flex items-center gap-3">
          {log.category && (
            <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: log.category.color + '33', color: log.category.color }}>
              {log.category.name}
            </span>
          )}
          <span className="text-gray-600 text-xs">
            {log.author?.name || log.author?.username} · {fmt(log.createdAt)}
          </span>
        </div>

        {/* 에디팅 툴바 */}
        {editing && (
          <EditorToolbar editor={editor} onImageUpload={() => fileInputRef.current?.click()} />
        )}
      </div>

      {/* 본문 */}
      <div className="flex-1 overflow-y-auto">
        {!log.content && !editing ? (
          <p className="text-gray-600 text-sm px-6 py-8">내용 없음</p>
        ) : (
          <div className={editing ? 'border-b border-gray-800' : 'rich-view px-6 py-5'}>
            <EditorContent editor={editor} />
          </div>
        )}
      </div>

      {/* 파일 첨부 */}
      <FileAttachments
        entityType="research-log"
        entityId={parseInt(id)}
        editor={editing ? editor : null}
        canUpload={editing}
      />

      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
    </div>
  );
}
